import { CommonModule, registerLocaleData } from "@angular/common";
import {
  HttpClient,
  HttpClientModule,
  HTTP_INTERCEPTORS
} from "@angular/common/http";
import {
  APP_INITIALIZER,
  CUSTOM_ELEMENTS_SCHEMA,
  NgModule,
  inject,
  provideAppInitializer
} from "@angular/core";
import { MAT_DATE_LOCALE } from "@angular/material/core";
import { MatPaginatorIntl } from "@angular/material/paginator";
import { BrowserModule } from "@angular/platform-browser";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { FullCalendarModule } from "@fullcalendar/angular";
import {
  TranslateLoader,
  TranslateModule,
  TranslateService
} from "@ngx-translate/core";
import { TranslateHttpLoader } from "@ngx-translate/http-loader";
import { StarRatingModule } from "./shared/components/star-rating/star-rating.module";
import { KeycloakAngularModule, KeycloakService } from "keycloak-angular";
import { IConfig, provideNgxMask } from "ngx-mask";
import { environment } from "../environments/environment";
import { AppRoutingModule } from "./app-routing.module";
import { AppComponent } from "./app.component";
import { CoreModule } from "./core/core.module";
import { SharedModule } from "./shared/shared.module";
import { PaginatorIntlService } from "@shared/components/paginator/paginator-intl.service";
import { LoadingDrivebModule } from "@shared/components/loading-driveb/loading-driveb.module";
import { EmptyRouteComponent } from "./empty-route/empty-route.component";
import {
  AuthInterceptor,
  DirectUrlEncodeInterceptor,
  ErrorInterceptor
} from "@core/interceptors";
import es from "@angular/common/locales/es";
import pt from "@angular/common/locales/pt";
import { CloseDetailsGuard } from "@core/guards/close-details.guard";
import { MatIconRegistry } from "@angular/material/icon";
import { WebSocketService } from "@core/websocket/web-socket.service";
import { rxStompServiceFactory } from "@core/websocket/rx-stomp-service-factory";
import { initializeApp, provideFirebaseApp } from "@angular/fire/app";
import {
  getRemoteConfig,
  provideRemoteConfig
} from "@angular/fire/remote-config";
import { AppConfigService } from "@core/services/app-config.service";
import { LoginComponent } from "./login/login.component";
import { initializeSsoAwareKeycloak } from "@core/services/sso-login.service";

registerLocaleData(es);
registerLocaleData(pt);

/** i18n Configurations */
export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, "/assets/i18n/", ".json");
}

/** Keycloack Adapter Configurations */
export function initializeKeycloak(keycloak: KeycloakService) {
  return () =>
    initializeSsoAwareKeycloak(keycloak, () =>
      keycloak.init({
        config: environment.keycloack,
        enableBearerInterceptor: true,
        initOptions: {
          checkLoginIframe: false
        }
      })
    );
}

const maskConfig: Partial<IConfig> = {
  validation: false
};

const interceptors = [
  {
    provide: HTTP_INTERCEPTORS,
    useClass: AuthInterceptor,
    multi: true
  },
  {
    provide: HTTP_INTERCEPTORS,
    useClass: DirectUrlEncodeInterceptor,
    multi: true
  },
  {
    provide: HTTP_INTERCEPTORS,
    useClass: ErrorInterceptor,
    multi: true
  }
];

@NgModule({
  declarations: [AppComponent, EmptyRouteComponent, LoginComponent],
  imports: [
    /**Angular IO*/
    AppRoutingModule,
    BrowserAnimationsModule,
    BrowserModule,
    provideFirebaseApp(() => initializeApp(environment.firebase)),
    provideRemoteConfig(() => getRemoteConfig()),
    CommonModule,
    HttpClientModule,
    LoadingDrivebModule,
    /** Smart Fleet Core and Shared Modules*/
    CoreModule.forRoot(),
    SharedModule,
    /**i18n*/
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient]
      }
    }),
    /**Keycloack*/
    KeycloakAngularModule,
    StarRatingModule,
    FullCalendarModule
  ],
  providers: [
    provideNgxMask(maskConfig),
    provideAppInitializer(() => {
      const initializerFn = initializeKeycloak(inject(KeycloakService));
      return initializerFn();
    }),
    {
      provide: MAT_DATE_LOCALE,
      useValue: "en-GB"
    },
    CloseDetailsGuard,
    ...interceptors,
    {
      provide: MatPaginatorIntl,
      useFactory: (translate) => {
        const service = new PaginatorIntlService();
        service.injectTranslateService(translate);
        return service;
      },
      deps: [TranslateService]
    },
    {
      provide: WebSocketService,
      useFactory: rxStompServiceFactory
    }
  ],

  bootstrap: [AppComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class AppModule {
  constructor(private iconRegistry: MatIconRegistry) {
    iconRegistry
      .registerFontClassAlias("bosch", "Bosch-Ic")
      .setDefaultFontSetClass("Bosch-Ic");
  }
}
